import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

export default function ConfirmarRecomendacion() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState(null); // "confirm" | "decline"
  const [relationship, setRelationship] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setNotFound(true); return; }
    (async () => {
      try {
        const { data } = await api.get(`/recommender-confirm/${token}`);
        setInfo(data);
        if (data.status && data.status !== "pending") setDone(data.status);
      } catch { setNotFound(true); }
    })();
  }, [token]);

  const submit = async (confirm) => {
    if (confirm && relationship.trim().length < 10) {
      toast.error("Cuéntanos de dónde conoces al postulante o por qué lo recomiendas (mínimo 10 caracteres).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/recommender-confirm/${token}`, { confirm, relationship: relationship.trim() || null });
      setDone(data.status);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const Frame = ({ children }) => (
    <div className="min-h-screen bg-background flex items-center justify-center p-5">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-border p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6"><ShieldCheck className="w-6 h-6 text-accent" /><span className="font-heading font-extrabold text-lg text-primary">EIM Becas</span></div>
        {children}
      </div>
    </div>
  );

  if (notFound) return <Frame><div className="text-center" data-testid="rec-confirm-notfound"><XCircle className="w-10 h-10 text-destructive mx-auto mb-3" /><h1 className="text-xl font-semibold mb-2">Enlace no válido</h1><p className="text-muted-foreground">Este enlace de confirmación no es válido o ya venció.</p></div></Frame>;
  if (!info) return <Frame><div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Frame>;

  if (done) {
    return (
      <Frame>
        <div className="text-center" data-testid="rec-confirm-done">
          {done === "confirmed" ? <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" /> : <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />}
          <h1 className="text-xl font-semibold mb-2">{done === "confirmed" ? "¡Gracias por tu recomendación!" : "Respuesta registrada"}</h1>
          <p className="text-muted-foreground">{done === "confirmed" ? `Registramos que recomiendas a ${info.applicant_name}. El comité lo tendrá en cuenta.` : "Gracias por responder. Tu respuesta fue registrada."}</p>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="text-center mb-6">
        <HeartHandshake className="w-10 h-10 text-accent mx-auto mb-3" />
        <h1 className="text-xl font-semibold">Hola {info.recommender_name}</h1>
        <p className="text-muted-foreground mt-1"><strong className="text-primary">{info.applicant_name}</strong> te indicó como uno de los 3 socios del EIM que lo recomiendan (solicitud {info.human_id}).</p>
      </div>

      {mode !== "confirm" && (
        <div className="space-y-3">
          <Button onClick={() => setMode("confirm")} className="btn-lift w-full h-12 rounded-xl" data-testid="btn-mode-confirm">Sí, lo recomiendo</Button>
          <Button onClick={() => submit(false)} disabled={busy} variant="outline" className="w-full h-12 rounded-xl border-2" data-testid="btn-decline">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "No lo conozco / no puedo recomendarlo"}
          </Button>
        </div>
      )}

      {mode === "confirm" && (
        <div className="space-y-3">
          <label className="text-sm font-medium">¿De dónde lo conoces o por qué lo recomiendas?</label>
          <Textarea className="rounded-xl" placeholder="Ej.: Somos socios hace años y compartimos en la comunidad…" value={relationship} onChange={(e) => setRelationship(e.target.value)} data-testid="rec-relationship" />
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setMode(null)} className="rounded-xl border-2">Volver</Button>
            <Button onClick={() => submit(true)} disabled={busy} className="btn-lift rounded-xl flex-1 h-11" data-testid="btn-confirm-submit">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar recomendación"}
            </Button>
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground text-center mt-6">Tu respuesta es confidencial y ayuda al comité de becas en su decisión.</p>
    </Frame>
  );
}
